import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileText, Plus } from 'lucide-react';
import type { PrescriptionWithRelations } from '@dpfs/shared';
import { prescriptionsService } from '@/services/prescriptions';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, StatusBadge, EmptyState, LoadingState } from '@/components/ui';

const PAGE_SIZE = 20;

// Statuses accepted by the API list filter (PARTIALLY_FULFILLED is not filterable server-side).
const FILTER_STATUSES = ['DRAFT', 'ACTIVE', 'FULFILLED', 'CANCELLED', 'EXPIRED'] as const;

function itemsSummary(rx: PrescriptionWithRelations): string {
  return (rx.items ?? [])
    .map((item) => item.drug?.tradeName || item.itemLabel)
    .filter(Boolean)
    .join(', ');
}

export function PrescriptionList() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['prescriptions', 'list', { page, status, fromDate, toDate }],
    queryFn: () =>
      prescriptionsService.list({
        page,
        limit: PAGE_SIZE,
        status: status || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const prescriptions = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = Math.max(pagination?.totalPages ?? 1, 1);
  const hasFilters = !!(status || fromDate || toDate);

  const resetFilters = () => {
    setStatus('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title={t('nav.myPrescriptions')}
        description={t('doctor.prescriptions.description')}
        actions={
          <Link to="/doctor/prescriptions/new" className="btn-primary">
            <Plus className="h-4 w-4" />
            {t('nav.newPrescription')}
          </Link>
        }
      />

      {/* Filters */}
      <div className="card mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
          <div>
            <label htmlFor="rx-status" className="label">
              {t('doctor.prescriptions.status')}
            </label>
            <select
              id="rx-status"
              className="input"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('doctor.prescriptions.allStatuses')}</option>
              {FILTER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`prescription.status.${s.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rx-from" className="label">
              {t('doctor.prescriptions.fromDate')}
            </label>
            <input
              id="rx-from"
              type="date"
              className="input"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <label htmlFor="rx-to" className="label">
              {t('doctor.prescriptions.toDate')}
            </label>
            <input
              id="rx-to"
              type="date"
              className="input"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={resetFilters}
              disabled={!hasFilters}
            >
              {t('doctor.prescriptions.resetFilters')}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <div className="p-5">
            <div className="alert-danger items-center justify-between">
              <span>{t('doctor.loadError')}</span>
              <button type="button" className="btn-secondary btn-sm" onClick={() => refetch()}>
                {t('doctor.retry')}
              </button>
            </div>
          </div>
        ) : prescriptions.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={t('doctor.prescriptions.empty')}
            description={t('doctor.prescriptions.emptyHint')}
            action={
              hasFilters ? (
                <button type="button" className="btn-secondary btn-sm" onClick={resetFilters}>
                  {t('doctor.prescriptions.resetFilters')}
                </button>
              ) : (
                <Link to="/doctor/prescriptions/new" className="btn-primary btn-sm">
                  <Plus className="h-4 w-4" />
                  {t('nav.newPrescription')}
                </Link>
              )
            }
          />
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('doctor.prescriptions.reference')}</th>
                    <th scope="col">{t('doctor.prescriptions.date')}</th>
                    <th scope="col">{t('doctor.patientLabel')}</th>
                    <th scope="col">{t('doctor.prescriptions.content')}</th>
                    <th scope="col">{t('doctor.prescriptions.expires')}</th>
                    <th scope="col">{t('doctor.prescriptions.status')}</th>
                    <th scope="col">
                      <span className="sr-only">{t('doctor.prescriptions.open')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {prescriptions.map((rx) => {
                    const docType = rx.documentType || 'MEDICATION';
                    const summary = itemsSummary(rx);
                    return (
                      <tr key={rx.id}>
                        <td className="whitespace-nowrap">
                          <Link to={`/doctor/prescriptions/${rx.id}`} className="link font-mono text-xs">
                            #{rx.id.slice(-8)}
                          </Link>
                          {docType !== 'MEDICATION' && (
                            <span className="badge-primary ms-2">
                              {t(`prescription.docTypesShort.${docType.toLowerCase()}`)}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap tabular-nums">
                          {new Date(rx.createdAt).toLocaleDateString(dateLocale)}
                        </td>
                        <td className="whitespace-nowrap font-mono text-xs">
                          {rx.patient ? patientIdentifier(rx.patient).masked : '—'}
                        </td>
                        <td className="max-w-xs">
                          <p className="truncate" title={summary}>
                            {summary || '—'}
                          </p>
                        </td>
                        <td className="whitespace-nowrap tabular-nums text-gray-500">
                          {rx.expiresAt ? new Date(rx.expiresAt).toLocaleDateString(dateLocale) : '—'}
                        </td>
                        <td className="whitespace-nowrap">
                          <StatusBadge status={rx.status} />
                        </td>
                        <td className="text-end">
                          <Link
                            to={`/doctor/prescriptions/${rx.id}`}
                            className="btn-icon"
                            aria-label={t('doctor.prescriptions.open')}
                          >
                            <ChevronRight className="h-4 w-4 rtl-flip" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <span>{t('doctor.prescriptions.total', { n: pagination?.total ?? prescriptions.length })}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 rtl-flip" />
                  {t('doctor.prescriptions.previous')}
                </button>
                <span className="px-1 tabular-nums">
                  {t('doctor.prescriptions.pageOf', { page, total: totalPages })}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t('doctor.prescriptions.next')}
                  <ChevronRight className="h-4 w-4 rtl-flip" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
